import { Box } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  EXPIRY_MS,
  IMPERSONATION_USER_EVENTS,
  PHONE_BEZEL_RADIUS,
  PHONE_BEZEL_WIDTH,
  RESIZE_HANDLE_STYLES,
  type ResizeDirection,
  WARNING_THRESHOLD_MS,
} from "../constants";
import { useImpersonationMessaging } from "../hooks/useImpersonationMessaging";
import { useSessionTimer } from "../hooks/useSessionTimer";
import { useWindowInteractions } from "../hooks/useWindowInteractions";
import { useImpersonationSessions } from "../ImpersonationSessionsProvider";
import type { ImpersonationSession } from "../types";
import { FloatingWindowHeader } from "./FloatingWindowHeader";
import { ClosingOverlay, ErrorOverlay } from "./ImpersonationWindowOverlays";

interface FloatingImpersonationWindowProps {
  session: ImpersonationSession;
}

const ALL_RESIZE_DIRECTIONS: ResizeDirection[] = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];

export function FloatingImpersonationWindow({ session }: FloatingImpersonationWindowProps) {
  const { adminUserId: employeeId } = useSession();
  const {
    bringToFront,
    closeSession,
    minimizeSession,
    toggleMaximizeSession,
    updateSessionExpiresAt,
    updateWindowPosition,
    updateWindowSize,
  } = useImpersonationSessions();
  const elapsed = useSessionTimer(session.startedAt);

  const remainingMs = session.expiresAt ? session.expiresAt - Date.now() : EXPIRY_MS - elapsed;
  const isWarning = remainingMs <= WARNING_THRESHOLD_MS && remainingMs > 0;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeKey, setIframeKey] = useState(0);

  const sessionOrigin = useMemo(() => new URL(session.url).origin, [session.url]);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isWorker = session.sessionType === "worker";
  const bezelWidth = isWorker ? PHONE_BEZEL_WIDTH : 0;
  // For workers the outer frame includes the phone bezel; for workplace it
  // matches the inner iframe size exactly so we can fill the viewport.
  const totalWidth = session.size.width + bezelWidth * 2;

  const forceClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeSession(session.id);
  }, [closeSession, session.id]);

  const handleClose = useCallback(() => {
    if (isClosing) {
      return;
    }

    setIsClosing(true);

    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: "impersonation:end" }, sessionOrigin);
    }

    closeTimeoutRef.current = setTimeout(() => {
      forceClose();
    }, 5_000);
  }, [isClosing, sessionOrigin, forceClose]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleExpiresAtUpdate = useCallback(
    (expiresAt: number) => {
      updateSessionExpiresAt(session.id, expiresAt);
    },
    [updateSessionExpiresAt, session.id]
  );

  const handleToggleMaximize = useCallback(() => {
    toggleMaximizeSession(session.id);
  }, [toggleMaximizeSession, session.id]);

  const { error, resetTokenState } = useImpersonationMessaging({
    employeeId,
    session,
    iframeRef,
    onSessionEnd: handleClose,
    onCleanupComplete: forceClose,
    onExpiresAtUpdate: handleExpiresAtUpdate,
  });

  const handleRefresh = useCallback(() => {
    resetTokenState();
    setIframeKey((previous) => previous + 1);
    logEvent(IMPERSONATION_USER_EVENTS.SESSION_REFRESHED, {
      employeeId,
      targetId: session.targetId,
      sessionType: session.sessionType,
      sessionId: session.id,
    });
  }, [employeeId, resetTokenState, session.targetId, session.sessionType, session.id]);

  const {
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
    isInteracting,
  } = useWindowInteractions({
    sessionId: session.id,
    sessionType: session.sessionType,
    position: session.position,
    size: session.size,
    updateWindowPosition,
    updateWindowSize,
  });

  // Bring floating workplace window to front when user clicks inside the iframe.
  // Cross-origin iframes don't propagate pointer/click events to the parent.
  // We poll document.activeElement to detect when our iframe gains focus.
  // Worker frames are excluded — they use a fixed z-index offset to stay on top.
  const wasFocusedRef = useRef(false);
  useEffect(() => {
    if (isWorker) {
      return undefined;
    }

    const interval = setInterval(() => {
      const isFocused = document.activeElement === iframeRef.current;
      if (isFocused && !wasFocusedRef.current) {
        bringToFront(session.id);
      }

      wasFocusedRef.current = isFocused;
    }, 200);

    return () => {
      clearInterval(interval);
      wasFocusedRef.current = false;
    };
  }, [bringToFront, session.id, isWorker, iframeKey]);

  const resizeDirections: ResizeDirection[] = isWorker ? ["se"] : ALL_RESIZE_DIRECTIONS;

  return (
    <Box
      sx={{
        position: "fixed",
        left: session.position.x,
        top: session.position.y,
        zIndex: (theme) => {
          const typeOffset = isWorker ? 100 : 0;
          return theme.zIndex.modal + 1 + typeOffset + session.zIndex;
        },
        display: session.isMinimized ? "none" : "flex",
        flexDirection: "column",
        userSelect: "none",
      }}
      onPointerDown={() => {
        bringToFront(session.id);
      }}
    >
      <FloatingWindowHeader
        session={session}
        isClosing={isClosing}
        isWarning={isWarning}
        error={error}
        remainingMs={remainingMs}
        totalWidth={totalWidth}
        showMaximizeButton={!isWorker}
        handleDragStart={handleDragStart}
        handleDragMove={handleDragMove}
        handleDragEnd={handleDragEnd}
        handleRefresh={handleRefresh}
        handleClose={handleClose}
        handleToggleMaximize={handleToggleMaximize}
        minimizeSession={minimizeSession}
      />

      <Box
        sx={{
          width: totalWidth,
          height: session.size.height + bezelWidth * 2,
          ...(isWorker
            ? {
                border: `${bezelWidth}px solid`,
                borderColor: "grey.800",
                borderRadius: `0 0 ${PHONE_BEZEL_RADIUS}px ${PHONE_BEZEL_RADIUS}px`,
              }
            : {
                border: "1px solid",
                borderColor: "grey.300",
                borderTop: "none",
              }),
          overflow: "hidden",
          boxShadow: 24,
          backgroundColor: "background.paper",
          position: "relative",
        }}
      >
        <Box
          key={iframeKey}
          ref={iframeRef}
          component="iframe"
          src={session.url}
          sx={{
            width: "100%",
            height: "100%",
            border: "none",
            display: isClosing || error ? "none" : "block",
          }}
          title={`Impersonation - ${session.targetName}`}
        />
        {isClosing && <ClosingOverlay />}
        {!isClosing && error && <ErrorOverlay error={error} onRetry={handleRefresh} />}
        {/*
          Transparent shield over the iframe while the user is dragging/resizing.
          Without it, pointer events that move into the iframe area get sent to
          the cross-origin document and the parent loses tracking of the gesture.
        */}
        {isInteracting && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              cursor: "inherit",
            }}
          />
        )}
      </Box>
      {/*
        Resize handles live on the OUTER container so the top edge / corners
        sit at the very top of the floating window (above the title bar) rather
        than only at the top of the iframe area. The 8px top edge cuts into the
        first 8px of the header drag region — that's intentional and matches
        OS-level window-resize behavior.
      */}
      {resizeDirections.map((direction) => (
        <Box
          key={direction}
          aria-label={`Resize ${direction}`}
          sx={RESIZE_HANDLE_STYLES[direction]}
          onPointerDown={handleResizeStart(direction)}
          onPointerMove={handleResizeMove}
          onPointerUp={handleResizeEnd}
        />
      ))}
    </Box>
  );
}
